<!-- TODO
	Check other print options
	Rename preference options to "printer-blah-blah"?
-->
<template>
	<div v-if="loading" class="tag-print-loading">
		<p>Checking printer settings...</p>
		<i class="fa fa-cog fa-spin"></i>
	</div>
	<div v-else class="tag-print">
		<div v-if="preferences.printerType == 'dymo'" class="error v-spaced">
			<b>ALERT:</b> Dymo discontinued the 450 series at the beginning of 2022 and is no longer publishing software updates for this model.<br>
			If your labels no longer print, it's likely that the Dymo Connect software is incompatible with a recent update to your operating system or browser.<br>
			You can still follow the instructions in our Dymo troubleshooting guide (<a href="https://cds-media.s3.us-west-2.amazonaws.com/downloads/Dymo+Printer+Troubleshooting.pdf" target="_blank" style="color: red;"><b><i>download PDF here</i></b></a>).<br>
			If the troubleshooting was unsuccessful, you will need to upgrade to a supported Zebra model (<a href="https://cds-media.s3.us-west-2.amazonaws.com/downloads/CDS-Flight-Deck-Compatible-POS-Hardware-&-Supplies.pdf" target="_blank" style="color: red;"><b><i>see supported devices here</i></b></a>).<br>
			Please do NOT buy a 500 series Dymo printer, as these are not compatible with our software. <br>
			Note that this issue does not affect Zebra printers.
		</div>
		<div v-if="preferences.printerType == 'dymo' && !dymoEnvironment.isBrowserSupported" class="error">Dymo does not currently support your browser.</div>
		<div v-else-if="preferences.printerType == 'dymo' && !dymoEnvironment.isWebServicePresent" class="error two-columns">
			<div>It looks like you don't have Dymo Web Service installed.<br>You can still view label xml or a PDF of your labels.</div>
			<div>
				<div class="button action" @click="connectToDymo()">Try Again</div>
			</div>
		</div>
		<div v-else-if="preferences.printerType == 'dymo' && !dymoEnvironment.isFrameworkInstalled" class="error">It looks like the Dymo framework isn't installed.</div>
		<div v-else-if="preferences.printerType == 'dymo' && dymoEnvironment.errorDetails" class="error">Oops! {{ dymoEnvironment.errorDetails }}.</div>
		<div class="settings" :class="{ open: settingsOpen }">
			<h5 @click="settingsOpen = !settingsOpen" style="position: absolute; top: 0; right: 2em;"><i class="fas fa-caret-right"></i> Settings ({{ preferences.printerType.capitalizeWords() }})</h5>
			<div v-show="settingsOpen" :class="preferences.zebraPrinterConnection === 'usb' ? 'settings-inner-zebra-usb' : 'settings-inner'">
			<!-- <div class="settings-inner"> -->
					<div class="container-printer-types">
					<div>
						<span class="label">Printer Type</span>
						<div class="control-group">
							<label class="radio first">
								<input type="radio" v-model="preferences.printerType" value="zebra">
								<span @click="savePrinterType('zebra')"><i class="fas fa-check-circle"></i>Zebra</span>
							</label>
							<label class="radio last">
								<input type="radio" v-model="preferences.printerType" value="dymo">
								<span @click="savePrinterType('dymo')"><i class="fas fa-check-circle"></i>Dymo</span>
							</label>
						</div>
					</div>
					<div v-if="preferences.printerType === 'zebra'">
						<span class="label">Connection Method</span>
						<div class="control-group">
							<label class="radio first">
								<input @change="savePrinterSettings()" type="radio" v-model="preferences.zebraPrinterConnection" value="usb">
								<span><i class="fas fa-check-circle"></i>USB</span>
							</label>
							<label class="radio last">
								<input @change=savePrinterSettings() type="radio" v-model="preferences.zebraPrinterConnection" value="network">
								<span><i class="fas fa-check-circle"></i>Network</span>
							</label>
						</div>
					</div>
				</div>
				<div v-if="preferences.printerType == 'dymo'" style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 3em; grid-column-gap: 2em;">
					<div>
						<span class="label">Default DYMO shelf tag tray</span>
						<div class="control-group">
							<label class="radio first">
								<input type="radio" v-model="preferences.posDefaultShelftagTray" value="left">
								<span><i class="fas fa-check-circle"></i>Left</span>
							</label>
							<label class="radio last">
								<input type="radio" v-model="preferences.posDefaultShelftagTray" value="right">
								<span><i class="fas fa-check-circle"></i>Right</span>
							</label>
						</div>
					</div>
					<div>
						<span class="label">Default DYMO box tag tray</span>
						<div class="control-group">
							<label class="radio first">
								<input type="radio" v-model="preferences.posDefaultBoxtagTray" value="left">
								<span><i class="fas fa-check-circle"></i>Left</span>
							</label>
							<label class="radio last">
								<input type="radio" v-model="preferences.posDefaultBoxtagTray" value="right">
								<span><i class="fas fa-check-circle"></i>Right</span>
							</label>
						</div>
					</div>
					<div>
						<span class="label">&nbsp;</span>
						<div :class="['button', saving ? 'disabled' : 'action']" @click="savePrinterSettings()">Save Default Trays</div>
					</div>
					<div style="padding: 1.75em 0;">
						{{ saveMessage }}
					</div>
				</div>
				<div v-if="preferences.printerType == 'zebra'">
					<!-- ZEBRA USB Printer -->
					<zebra-usb-printer v-show="preferences.zebraPrinterConnection ==='usb'"
						:savedPrinterInfo="preferences.printerUSBInfo.printers"
						ref="zebraUSBRef"
						@save-usb-printer-info="usbPrinterSave"
						@update-usb-printer-status="usbPrinterUpdate"
					/>
					<div v-if="preferences.zebraPrinterConnection === 'network'" style="display: grid; grid-template-columns: 2.5fr 1fr 1fr 1fr 3em; grid-column-gap: 2em;">
						<div>
							To print to a Zebra printer on your own network, you will need to install a third-party Chrome extension called "Zebra Printing" <a class="printer-option" href="https://chrome.google.com/webstore/detail/zebra-printing/ndikjdigobmbieacjcgomahigeiobhbo" target="_blank">available here</a>.
						</div>
						<div>
							<span class="label">Zebra printer IP address</span>
							<input type="text" v-model="preferences.primaryZebraPrinterIpAddress">
						</div>
						<div>
							<span class="label">&nbsp;</span>
							<div :class="['button', saving ? 'disabled' : 'action']" @click="savePrinterSettings()">Save IP Address</div>
						</div>
						<div style="padding: 1.75em 0;">
							{{ saveMessage }}
						</div> 
					</div>
				</div>
			</div>
		</div>
		<div class="printer-list" v-if="enableDymoConnect && preferences.printerType == 'dymo'">
			<div v-if="printers.length > 0" style="display: inline-block;">
				<p>Available printer(s):</p>
				<p v-for="(printer, index) in printers" :key="index">
					<template v-if="printer.isConnected"><span :class="['printer-item', { 'selected-printer': selectedPrinter == printer.name }]" @click="selectedPrinter = printer.name">{{ printer.name }}</span> <i class="fas fa-info-circle" @click="debugInfoIndex != null ? debugInfoIndex = null : debugInfoIndex = index"></i></template>
					<template v-else><span class="printer-item not-connected">{{ printer.name }}</span> <span class="small">(not connected)</span> <i class="fas fa-info-circle" @click="debugInfoIndex != null ? debugInfoIndex = null : debugInfoIndex = index"></i></template>
				</p>
				<div v-if="debugInfoIndex !== null && Object.keys(debugInfo).length > 0" style="margin: 1em 0 0 1em;">
					<b>Printer Info</b>
					<div class="indent">
						Connected: {{ debugInfo.isConnected }}<br />
						Local: {{ debugInfo.isLocal }}<br />
						Twin Turbo: {{ debugInfo.isTwinTurbo }}<br />
						Model Name: {{ debugInfo.modelName }}<br />
						Printer Type: {{ debugInfo.printerType }}
					</div>
				</div>
			</div>
			<div :class="['pointer', 'printer-option', { refreshing: refreshingPrinterList }]" @click="initDymoPrinters()"><i class="fas fa-redo-alt"></i> Refresh printer list</div>
		</div>
		<div v-if="preferences.printerType == 'dymo' && enableDymoConnect && connectedPrinters == 0" class="two-columns" style="margin: 0.5em 0;">
			<div>
				<div class="note"><i class="fas fa-ban"></i> We don't see a connected Dymo label printer. <div class="pointer printer-option" @click="showPrinterOptions = !showPrinterOptions">Here are some things you can try...</div></div>
				<div v-if="showPrinterOptions">
					<ul style="list-style: inside; margin: 0 0 0 1em; line-height: 1.5em;">
						<li>Check your connections and then <span :class="['pointer', 'printer-option', { refreshing: refreshingPrinterList }]" @click="initDymoPrinters()">refresh the printer list...</span></li>
						<li><a href="/dashboard/help-center/articles/131" class="printer-option" target="_blank">See how to install a Dymo printer on this computer...</a></li>
					</ul>
				</div>
			</div>
		</div>
	
		<table class="label-buttons" style="width: auto;">
			<thead>
				<tr>
					<th v-if="localSerialNumbers.length > 0">Current Serial Number</th>
					<th v-if="selectedPrinter || preferences.printerType == 'zebra'">Copies</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td valign="top" v-if="localSerialNumbers.length > 0">
						<select v-model="selectedSerialNumber">
							<option value="">Select a serial number</option>
							<option v-for="(serialNumber, index) in localSerialNumbers" :value="serialNumber" :key="index">{{ serialNumber }}</option>
						</select>
					</td>
					<td valign="top" v-if="selectedPrinter || preferences.printerType == 'zebra'">
						<input class="copies" :disabled="!selectedPrinter && this.preferences.printerType === 'dymo'" type="number" min="1" v-model="tagCopies">
					</td>
				</tr>
			</tbody>
		</table>
	
		<table class="label-buttons">
			<thead>
				<tr>
					<th>Tag Type</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td valign="top">
						<div class="print-buttons">
							<div>
								<!-- <div :class="['button', (!printEnabled || printing.shelf || checkUsbPrinterLabels('shelf')) ? 'disabled' : 'action']" @click="printTag('shelf', 'printer')"><span>Shelf </span> <span>1" x 1<sup>1</sup>&frasl;<sub>2</sub>"</span></div> -->

								<div :class="['button', (!printEnabled || printing.shelf || checkUsbPrinterLabels('shelf')) ? 'disabled' : 'action']" @click="printTag('shelf', 'printer')"><span>Shelf </span><span>1" x 1-1/2"</span></div>
								<div class="note">Label #30347</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing.shelf ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('shelf', 'download')">
									<i class="fas fa-download" title="Download Label File"></i><span>Download Label</span>
								</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing.shelf ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('shelf', 'pdf')">
									<i class="far fa-file-pdf" title="Generate Label PDF"></i><span>PDF</span>
								</div>
							</div>
							<div>
								<div :class="['button', (!printEnabled || printing.shelf_wide || checkUsbPrinterLabels('shelf_wide')) ? 'disabled' : 'action']" @click="printTag('shelf_wide', 'printer')"><span>Shelf </span><span>1" x 2-1/8"</span></div>
								<div class="note">Label #30336</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing.shelf_wide ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('shelf_wide', 'download')">
									<i class="fas fa-download" title="Download Label File"></i><span>Download Label</span>
								</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing.shelf_wide ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('shelf_wide', 'pdf')">
									<i class="far fa-file-pdf" title="Generate Label PDF"></i><span>PDF</span>
								</div>
							</div>
							<div v-if="getProductType == 'FIREARMS'">
								<div :class="['button', (!printEnabled || printing['sequence_small'] || checkUsbPrinterLabels('sequence_small')) ? 'disabled' : 'action']" @click="printTag('sequence_small', 'printer')"><span>Sequence </span><span>1" x 1-1/2"</span></div>
								<div class="note">Label #30347</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing['sequence_small'] ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('sequence_small', 'download')">
									<i class="fas fa-download" title="Download Label File"></i><span @click="printTag('sequence_small', 'download')" >Download Label</span>
								</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing['sequence_small'] ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('sequence_small', 'pdf')">
									<i class="far fa-file-pdf" title="Generate Label PDF"></i><span>PDF</span>
								</div>
							</div>
							<div v-if="getProductType == 'FIREARMS'">
								<div :class="['button', (!printEnabled || printing['sequence'] || checkUsbPrinterLabels('sequence')) ? 'disabled' : 'action']" @click="printTag('sequence', 'printer')"><span>Sequence </span><span>1-1/8" x 3-1/2"</span></div>

								<div class="note">Label #30252</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing['sequence'] ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('sequence', 'download')">
									<i class="fas fa-download" title="Download Label File"></i><span @click="printTag('sequence', 'download')" >Download Label</span>
								</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing['sequence'] ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('sequence', 'pdf')">
									<i class="far fa-file-pdf" title="Generate Label PDF"></i><span>PDF</span>
								</div>
							</div>
							<div v-if="getProductType != 'SERVICES'">
								<div :class="['button', (!printEnabled || printing['box'] || checkUsbPrinterLabels('box')) ? 'disabled' : 'action']" @click="printTag('box', 'printer')"><span>Box </span><span>1-1/8" x 3-1/2"</span></div>
								<div class="note">Label #30252</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing['box'] ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('box', 'download')">
									<i class="fas fa-download" title="Download Label File"></i><span @click="printTag('box', 'download')" >Download Label</span>
								</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing['box'] ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('box', 'pdf')">
									<i class="far fa-file-pdf" title="Generate Label PDF"></i><span>PDF</span>
								</div>
							</div>
							<div v-if="getProductType == 'FIREARMS'">
								<div :class="['button', (!printEnabled || printing['gun'] || checkUsbPrinterLabels('gun')) ? 'disabled' : 'action']" @click="printTag('gun', 'printer')"><span>Gun </span><span>1-1/8" x 3-1/2"</span></div>
								<div class="note">Label #30252</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing['gun'] ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('gun', 'download')">
									<i class="fas fa-download" title="Download Label File"></i><span @click="printTag('gun', 'download')" >Download Label</span>
								</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing['gun'] ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('gun', 'pdf')">
									<i class="far fa-file-pdf" title="Generate Label PDF"></i><span>PDF</span>
								</div>
							</div>
							<div v-if="showGunTagTall && getProductType == 'FIREARMS'">
								<div :class="['button', (!printEnabled || printing['gun_tall'] || checkUsbPrinterLabels('gun_tall')) ? 'disabled' : 'action']" @click="printTag('gun_tall', 'printer')"><span>Gun </span><span>3-1/2" x 1-1/8"</span></div>
								<div class="note">Label #30252</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing['gun_tall'] ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('gun_tall', 'download')">
									<i class="fas fa-download" title="Download Label File"></i><span @click="printTag('gun_tall', 'download')" >Download Label</span>
								</div>
								<div v-if="preferences.printerType == 'dymo'" :class="['alt-print-buttons', printing['gun_tall'] ? 'alt-print-disabled' : 'alt-print-action']" @click="printTag('gun_tall', 'pdf')">
									<i class="far fa-file-pdf" title="Generate Label PDF"></i><span>PDF</span>
								</div>
							</div>
							<div v-if="preferences.printerType == 'zebra' && preferences.zebraPrinterConnection === 'usb'">
								<div :class="['button', (!printEnabled || printing['zebra_4x6'] || checkUsbPrinterLabels('zebra_4x6')) ? 'disabled' : 'action']" @click="printTag('zebra_4x6', 'printer')">
									<span>ZD421 </span><span> 4&quot;x 6&quot;</span></div>
							</div>
						</div>
					</td>
				</tr>
			</tbody>
		</table>
	
		<message-modal v-if="printingError" :message="printingError" errorStatus="alert" @dismiss="printingError = ''" />
	
		<iframe id="download" src="" style="display: none;"></iframe>
	
	</div>
</template>
	
<script>
import ZebraUSBPrinter from '@/components/ZebraUSBPrinter.vue';

export default {
	name: 'tag-print',
	props: ['productDetail', 'serialNumbers'],
	components: {'zebra-usb-printer': ZebraUSBPrinter},
	data() {
		return {
			loading: true,
			whichBrowser: '',
			enableDymoConnect: false,
			dymoEnvironment: {},
			errorDetails: null,
			connectedPrinters: 0,
			printers: [],
			refreshingPrinterList: false,
			debugInfoIndex: null,
			selectedPrinter: null,
			showPrinterOptions: false,
			tagCopies: 1,
			selectedSerialNumber: '',
			printing: {
				shelf: false,
				shelf_wide: false,
				box: false,
				sequence: false,
				sequence_small: false,
				gun: false,
				zebra_4x6: false,
			},
			message: '',
			tagImage: null,
			zebraPrinterPresent: false.status,
			settingsOpen: false,
			customTagOpen: false,
			customTagZPL: null,
			preferences: {
				printerType: '',
				posDefaultBoxtagTray: '',
				posDefaultShelftagTray: '',
				primaryZebraPrinterIpAddress: '',
				zebraPrinterConnection: '',
				printerUSBInfo: '',
				zebraLabelFormat: ''
			},
			saving: false,
			saveMessage: '',
			printingError: '',
			productTypeCode: '',
			// USB Printer
			usbEnabledTagTypes: [],
			usbActivePrinters: 0,
		}
	},
	created() {
		if (this.productDetail.info) {
			this.productTypeCode = this.productDetail.info.product_type_code
		} else if (this.productDetail.product_type_code) {
			this.productTypeCode = this.productDetail.product_type_code
		} else if (this.productDetail.ffl_log_book_entry_id) {
			this.productTypeCode = 'FIREARMS'
		}
		this.getPrinterPreferences()
		if (this.localSerialNumbers.length > 0) {
			this.selectedSerialNumber = this.localSerialNumbers[0]
		}
	},
	mounted() {
		window.addEventListener("message", (event) => {
			if (!event.data.CDSZebraPrintResponse) {
				return;
			}
			if (event.data.CDSZebraPrintResponse.status == 200) {
				this.zebraPrinterPresent = true
			} else {
				this.zebraPrinterPresent = false
			}
		});
	},
	watch: {
		'localSerialNumbers' (newVal, oldVal) {
			this.selectedSerialNumber = this.localSerialNumbers[this.localSerialNumbers.length - 1]
		},
		'productDetail' (newVal, oldVal) {
			if (this.productDetail.info) {
				this.productTypeCode = this.productDetail.info.product_type_code
			} else if (this.productDetail.product_type_code) {
				this.productTypeCode = this.productDetail.product_type_code
			} else if (this.productDetail.ffl_log_book_entry_id) {
				this.productTypeCode = 'FIREARMS'
			}
		},
	},
	computed: {
		localSerialNumbers() {
			if (!this.serialNumbers) return []
			if (Array.isArray(this.serialNumbers)) {
				return this.serialNumbers.filter(serialNumber => serialNumber != '')
			}
			if (typeof this.serialNumbers === 'object') {
				return Object.values(this.serialNumbers).filter(serialNumber => serialNumber != '')
			}
		},
		debugInfo() {
			return this.printers[this.debugInfoIndex]
		},
		showGunTagTall() {
			return this.preferences.printerType == 'dymo' && (this.getClientId == 170 || this.getClientId == 221)
		},
		getProductType() {
			switch (this.productTypeCode) {
				case 'CLASSES':
				case 'MEMBERSHIPS':
				case 'RANGE_FEES':
				case 'GIFT_CARDS':
				case 'SERVICES':
				case 'RANGE':
					return 'SERVICES';
				case 'FIREARMS':
				case 'HANDGUNS':
				case 'NFA':
				case 'TRANSFERS':
				case 'RENTALS':
					return 'FIREARMS';
				default:
					return 'PRODUCTS';
			}
		},
		printEnabled() {
			if ((this.productDetail.product_id || this.productDetail.ffl_log_book_entry_id) && this.preferences.printerType == 'dymo' && this.enableDymoConnect && this.connectedPrinters > 0) { return true }
			if ((this.productDetail.product_id || this.productDetail.ffl_log_book_entry_id) && this.preferences.printerType == 'zebra' && this.preferences.zebraPrinterConnection === 'network' && this.preferences.primaryZebraPrinterIpAddress) { return true }
			if ((this.productDetail.product_id || this.productDetail.ffl_log_book_entry_id) && this.preferences.printerType == 'zebra' && this.preferences.zebraPrinterConnection === 'usb') { 
				return true
			}
			return false;
		},
	},
	methods: {
		getPrinterPreferences() {
			this.printers = []
			this.apiCall({ destination: 'get_item_preference', data: { preference_code: 'POS_DEFAULT_BOXTAG_TRAY,POS_DEFAULT_SHELFTAG_TRAY,PRIMARY_ZEBRA_PRINTER_IP_ADDRESS,TAG_PRINTER_TYPE,PRINTER_ZEBRA_CONNECTION,PRINTER_ZEBRA_USB_INFO,PRINTER_ZEBRA_LABEL_FORMAT'}}).then(obj => {
				this.preferences.posDefaultBoxtagTray = obj.POS_DEFAULT_BOXTAG_TRAY
				this.preferences.posDefaultShelftagTray = obj.POS_DEFAULT_SHELFTAG_TRAY
				this.preferences.primaryZebraPrinterIpAddress = obj.PRIMARY_ZEBRA_PRINTER_IP_ADDRESS
				this.preferences.zebraPrinterConnection = obj.PRINTER_ZEBRA_CONNECTION
				this.preferences.printerUSBInfo = obj.PRINTER_ZEBRA_USB_INFO ? JSON.parse(obj.PRINTER_ZEBRA_USB_INFO) : ''
				this.preferences.printerType = obj.TAG_PRINTER_TYPE || localStorage.getItem('tag_printer_type') || 'zebra'
				this.preferences.zebraLabelFormat = obj.PRINTER_ZEBRA_LABEL_FORMAT || ''
				if (this.preferences.printerType == 'zebra') { //  && 
					if ((this.preferences.zebraPrinterConnection === 'network' && !this.preferences.primaryZebraPrinterIpAddress) ||
						(this.preferences.zebraPrinterConnection === 'usb' && this.preferences.printerUSBInfo && this.preferences.printerUSBInfo.printers.length === 0)
						|| !this.preferences.zebraPrinterConnection) {
						// USB Zebra active connections abstracted to usbPrinterUpdate
						this.settingsOpen = true
						this.loading = false
					} else {
						this.connectToZebra()
					}
				} else {
					this.connectToDymo()
				}
			}).catch(err => {
				console.log(err);
			})
		},
		connectToZebra() {
			window.postMessage({
				type: "zebra_print_label",
				url: `http://${this.preferences.primaryZebraPrinterIpAddress}`
			}, "*");
			this.loading = false
		},
		connectToDymo() {
			dymo.label.framework.init()
			this.dymoEnvironment = dymo.label.framework.checkEnvironment()
			if (this.dymoEnvironment.isBrowserSupported && this.dymoEnvironment.isFrameworkInstalled && this.dymoEnvironment.isWebServicePresent) {
				this.enableDymoConnect = true
				setTimeout(() =>{
					this.initDymoPrinters()
				}, 500)
			} else {
				this.enableDymoConnect = false
				this.loading = false
			}
		},
		initDymoPrinters() {
			this.connectedPrinters = 0
			this.selectedPrinter = null
			this.refreshingPrinterList = true
			this.selectedPrinter = ''
			var printers = dymo.label.framework.getPrinters()
			// are there printers, and are any connected?
			this.printers = printers
			if (printers.length > 0) {
				for (let i = 0; i < printers.length; i++) {
					if (printers[i].isConnected) {
						this.connectedPrinters++
						this.selectedPrinter = this.printers[i]['name']
					}
				}
			}
			setTimeout(() => { this.refreshingPrinterList = false }, 500)
			this.loading = false
		},

		printTag(type, output) {
			if (output == 'printer' && !this.printEnabled) { return }
			if (this.printing[type]) { return }
			if ((output == 'download' || output == 'preview') && this.preferences.printerType == 'zebra') { return }
			if (this.preferences.printerType == 'dymo' && !this.enableDymoConnect && output == 'printer') { return }
			if (this.preferences.printerType == 'dymo' && !this.enableDymoConnect && output == 'preview') { return }
			if (this.preferences.printerType === 'zebra' && this.preferences.zebraPrinterConnection === 'usb'
				&& !this.usbEnabledTagTypes.includes(type)
			) { return }
			if (output == 'printer' && this.preferences.printerType == 'zebra' && this.preferences.zebraPrinterConnection === 'usb' && (!this.preferences.printerUSBInfo || this.usbEnabledTagTypes.length === 0)) {
				this.message = "Please setup your Zebra Printer USB settings"
				this.settingsOpen = true
				return
			}
			if (output == 'printer' && this.preferences.printerType == 'zebra' && this.preferences.zebraPrinterConnection === 'network' && !this.preferences.primaryZebraPrinterIpAddress) {
				this.message = "Please enter your Zebra Printer IP address in settings"
				this.settingsOpen = true
				return
			}
			this.printing[type] = true
			this.message = ""
			let postData = {
				primary_id: this.productDetail.product_id,
				ffl_log_book_entry_id: this.productDetail.ffl_log_book_entry_id,
				tag_type: type
			}
			// prepares postData specifically for Receiving Firearms for non-inv/transfer
			if (this.productDetail.receiving_non_inventory) {
				postData.primary_id = ''
				postData.ffl_log_book_entry_id = this.productDetail.entryBySerial[this.selectedSerialNumber]
			}
			if (type == 'sequence' || type == 'box' || type == 'gun' || type == 'gun_tall' || type == 'sequence_small' || type == 'zebra_4x6') {
				postData.serial_number = this.selectedSerialNumber
			}
			postData.zpl = this.preferences.printerType == 'zebra'
			postData.tag_copies = this.tagCopies
			postData.output = output
			this.apiCall({ destination: 'get_tag_xml', data: postData }).then(obj => {
				this.printing[type] = false
				if (obj.tag_xml) {
					var tag = dymo.label.framework.openLabelXml(obj.tag_xml)
					var printParams = {}
					if (output == 'printer' && this.dymoEnvironment.isWebServicePresent && this.selectedPrinter) {
						if (type == 'shelf' || type == 'shelf_wide' || type == 'sequence_small') {
							if (obj.default_shelf_tag_tray == 'right') {
								printParams.twinTurboRoll = dymo.label.framework.TwinTurboRoll.Right
							} else {
								printParams.twinTurboRoll = dymo.label.framework.TwinTurboRoll.Left
							}
						} else {
							if (obj.default_box_tag_tray == 'right') {
								printParams.twinTurboRoll = dymo.label.framework.TwinTurboRoll.Right
							} else {
								printParams.twinTurboRoll = dymo.label.framework.TwinTurboRoll.Left
							}
						}
						for (var i = 0; i < this.tagCopies; i++) {
							var printJob = tag.print(this.selectedPrinter, dymo.label.framework.createLabelWriterPrintParamsXml(printParams))
						}
					} else if (output == 'preview' && this.dymoEnvironment.isWebServicePresent) {
						printParams.shadowDepth = 0
						this.tagImage = tag.render(dymo.label.framework.createLabelRenderParamsXml(printParams), ' ')
					}
				} else if (obj.tag_zpl && output == 'printer') {
					if (this.preferences.zebraPrinterConnection === 'network') {
						window.postMessage({
							type: "zebra_print_label",
							zpl: obj.tag_zpl,
							url: `http://${this.preferences.primaryZebraPrinterIpAddress}/pstprnt`
						}, "*");
					} else {
						this.$refs.zebraUSBRef.printTag(obj.tag_zpl, type) 
					}
				} else if (output == 'pdf' && obj.download_url) {
					var labelURL = obj.download_url
					var showLabel = window.open(labelURL, "Inventory Tag", '_blank');
					if (!showLabel) {
						alert("Your browser didn't allow this PDF to open in a new window! Set your browser's preferences to allow popups and then try printing again.");
					}
				} else if (output == 'download' && obj.download_url) {
					document.getElementById("download").src = this.getDownloadEndpoint + "?file_url=" + obj.download_url + "&output_file_name=" + obj.file_name
					setTimeout(() => {
						this.showDownlaodForms = false
						this.downloadMessage = ''
						this.saving = false
					}, 3000)
				} else {
					this.message = "Tag not generated"
				}
			}).catch(err => {
				console.log(err)
				this.printingError = 'There was an error printing this label.'
				if (this.preferences.printerType == 'dymo') {
					this.printingError += '<br>You can download the label and print directly using your Dymo Connect app.'
				}
			})
		},
		printCustomTag() {
			if (!this.customTagZPL) return
			this.apiCall({ destination: 'get_custom_tag_zpl', data: { primary_id: this.productDetail.product_id, serial_number: this.selectedSerialNumber, custom_tag_zpl: this.customTagZPL } }).then(obj => {
				window.postMessage({
					type: "zebra_print_label",
					zpl: obj.tag_zpl,
					url: `http://${this.preferences.primaryzebraPrinterIpAddress}/pstprnt`
				}, "*");
			}).catch(err => {
				console.log(err);
			})
		},
		savePrinterType(type) {
			localStorage.setItem('tag_printer_type', type)
			if (type == 'dymo') {
				this.connectToDymo()
			} else {
				this.connectToZebra()
			}
			this.saving = true
			this.apiCall({ destination: 'save_item_preference', data: { preference_code: 'TAG_PRINTER_TYPE', value: type } }).then(obj => {
				this.saving = false
			}).catch(err => {
				this.saving = false
				console.log(err);
			})
		},
		savePrinterSettings() {
			if (this.saving) return
			this.saveMessage = ''
			this.saving = true
			this.apiCall({ destination: 'save_item_print_tag_settings', data: this.preferences}).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.saveMessage = 'Saved!'
				} else {
					this.saveMessage = obj.message
				}
				if (this.preferences.printerType === 'zebra' && this.preferences.zebraPrinterConnection === 'usb') {
					this.$refs.zebraUSBRef.sendMessage(this.saveMessage);
				}
			}).catch(err => {
				this.saving = false
				console.log(err);
			})
		},
		openTagImg() {
			let win = window.open()
			let image = new Image
			image.src = 'data:image/png;base64,' + this.tagImage
			win.document.body.append(image)
		},
		usbPrinterSave(printerInfo) { 
			this.preferences.printerUSBInfo = printerInfo
			this.usbPrinterUpdate(printerInfo.printers, true)
			this.savePrinterSettings()
		},
		checkUsbPrinterLabels(tagType) {
			if (this.preferences.printerType === 'dymo' || (this.preferences.printerType === 'zebra' && this.preferences.zebraPrinterConnection === 'network')
				|| !this.preferences.zebraPrinterConnection) {
				return false 
			}
			// Since we only we use this to disable, we want the opposite
			return !this.usbEnabledTagTypes.includes(tagType)
		},
		usbPrinterUpdate(printers, postInitChange=false) {
			this.usbEnabledTagTypes = [];
			this.usbActivePrinters = 0;
			if (Array.isArray(printers)) {
				printers.forEach(printer => {
					// Change assumes printer is already connected
					if (printer.connected || postInitChange && !printer.hasOwnProperty('connected')) {
						this.usbActivePrinters += 1;
						if (printer.tagType === 'large') {
							this.usbEnabledTagTypes.push('box', 'sequence', 'gun')
						} else {
							this.usbEnabledTagTypes.push(printer.tagType)
						}
					}
				})
			}
			if ((postInitChange || this.usbActivePrinters === 0) && this.preferences.zebraPrinterConnection === 'usb') {
				this.settingsOpen = true;
			} else if (this.preferences.zebraPrinterConnection === 'usb') {
				this.settingsOpen = false;
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.tag-print-loading {
	margin: 2em;
	text-align: center;
	.fa { font-size: 2em; }
}
.container-printer-types {
	display: flex;
	gap: 10vw;
	width: 40vw;
	position: relative;
}
.tag-print {
	select.printers { max-width: 24em; }
	table.label-buttons {
		width: 100%;
		border-collapse: collapse;
		margin: 0 0 1em 0;
		th { color: #666; padding: 0 0 0.25em 0; }
		td { padding: 0 0.5em 0 0; }
		select { margin: 0; width: 12em; }
		input { margin: 0; width: 5em;  }
		th.warning { color: #333; padding: 0.25em; }
	}
	div.note { margin: 1em 0 0 0; }
	.printer-list {
		margin: 0 0 1em 0;
		p { display: inline-block; margin: 0 0.5em 0 0; padding: 0 0.5em 0 0; border-right: 1px solid #666; }
		p:last-of-type { border-right: 0 none; }
		.printer-item, .fas { cursor: pointer; color: #777; }
		.printer-item.not-connected { cursor: not-allowed; }
	}
	.selected-printer {
		font-weight: bold;
	}
	.printer-option {
		font-weight: bold;
		color: #57b1e2;
		cursor: pointer;
		.fas { color: #57b1e2; }
	}
	.printer-option.refreshing {
		color: #ccc;
		.fas { color: #ccc; }
	}
	.printers-debug-info {
		font-size: 0.8em;
		text-transform: lowercase;
		text-decoration: underline;
		color: #666;
	}
	.debug-info {
		border: 1px solid #ccc;
		padding: 1em;
		margin: 1em;
	}
	.not-allowed {
		cursor: not-allowed !important;
	}
	.print-buttons {
		display: grid;
		grid-template-columns: 1fr 1fr 1fr;
		grid-gap: 1.5em;
		@media all and (min-width: 1500px) {
			grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
		}
		.button {
			width: 100%;
			margin: 0;
			font-weight: 600;
			span:nth-of-type(1) {
				text-transform: uppercase;
    		}
    		span:nth-of-type(2) {
        		text-transform: lowercase;
    		}
		}
		.alt-print-buttons {
			margin: 0.5em 0 0 0;
			display: grid;
			grid-template-columns: 1.5em 1fr;
			align-items: center;
			line-height: 1.25em;
			color: #666;
			cursor: pointer;
			span { font-size: 0.9em; }
			&:hover {
				color: #333;
			}
		}
		.alt-print-disabled {
			color: #ccc;
			cursor: not-allowed;
			&:hover {
				color: #ccc;
			}
			.fas, .far, .fas:hover, .far:hover {
				color: #ccc;
				cursor: not-allowed;
			}
		}
	}
	.extra-alt-print-buttons {
		display: grid;
		grid-template-columns: 1.5em 1fr;
		align-items: center;
		cursor: pointer;
		color: #666;
		span { font-size: 0.9em; }
		&:hover {
			color: #333;
		}
	}
	.control-group {
		min-width: 190px;
	}
	.settings, .custom-tag {
		h5 {
			margin: 0;
			cursor: pointer;
		}
		.settings-inner, .custom-tag-inner {
			transition: all 0.4s ease;
			height: 0;
			overflow: auto;
		}
		.fa-caret-right {
			transition: all 0.4s ease;
			font-size: 1.3rem;
			margin: 0 0.25em 0 0;
		}
		&.open {
			.fa-caret-right { transform: rotate(90deg); }
		}
		span.label {
			display: block;
		}
		label.radio { margin-right: 0; }
		input, select {
			max-width: 10em;
		}
	}
	.settings {
		position: relative;
		&.open {
			.settings-inner {
				margin-top: 1rem;
				height: 10em;
			}
			.settings-inner-zebra-usb {
				margin-top: 1rem;
				height: clamp(17.5rem, 25vh, 30rem);
			}
		}
	}
	.custom-tag {
		textarea {
			display: block;
			width: 20rem;
			height: 20rem;
		}
		&.open {
			.custom-tag-inner {
				height: 25rem;
			}
		}
	}
	i.disabled {
		background-color: white;
		color: #999;
		&:hover { color: #999; filter: none; }
	}
}
</style>
